import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiDownload, FiSmartphone, FiMonitor, FiCheck, FiStar } from 'react-icons/fi';
import { SiWindows, SiAndroid, SiApple } from 'react-icons/si';
import { getFileSizeById, FILE_SIZES } from '../utils/fileSizes';

const Download = () => {
  const [detectedPlatform, setDetectedPlatform] = useState('');
  const [downloadStarted, setDownloadStarted] = useState(false);
  const [manifest, setManifest] = useState(null);
  const [fileSizes, setFileSizes] = useState({
    windows: 'Loading...',
    android: 'Loading...',
    macos: 'Loading...'
  });

  // Platform detection
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const platform = navigator.platform.toLowerCase();
    
    if (userAgent.includes('android')) {
      setDetectedPlatform('android');
    } else if (platform.includes('win')) {
      setDetectedPlatform('windows');
    } else if (platform.includes('mac')) {
      setDetectedPlatform('macos');
    } else if (userAgent.includes('iphone') || userAgent.includes('ipad')) {
      setDetectedPlatform('ios');
    } else if (userAgent.includes('linux')) {
      setDetectedPlatform('linux');
    } else {
      setDetectedPlatform('windows'); // Default fallback
    }
  }, []);

  // Function to fetch file size dynamically
  const fetchFileSize = async (url, platform) => {
    try {
      // For Google Drive links, use our dynamic file size system
      if (url.includes('drive.google.com')) {
        // Extract file ID from Google Drive URL
        const fileIdMatch = url.match(/id=([a-zA-Z0-9_-]+)/);
        if (fileIdMatch) {
          const fileId = fileIdMatch[1];
          
          // First try to get size from our dynamic file size system
          const knownSize = getFileSizeById(fileId);
          if (knownSize) {
            return knownSize;
          }
          
          // Option: Try Google Drive API if available (future enhancement)
          const apiKey = process.env.REACT_APP_GOOGLE_DRIVE_API_KEY;
          if (apiKey) {
            try {
              const apiUrl = `https://www.googleapis.com/drive/v3/files/${fileId}?fields=size&key=${apiKey}`;
              const response = await fetch(apiUrl);
              const data = await response.json();
              if (data.size) {
                const sizeInMB = (parseInt(data.size) / (1024 * 1024)).toFixed(1);
                return `${sizeInMB} MB`;
              }
            } catch (apiError) {
              console.log('Google Drive API error, using fallback:', apiError);
            }
          }
        }
        
        // Fallback to platform defaults from our file size system
        return FILE_SIZES.platformFallbacks[platform] || 'Unknown';
      }
      
      // For regular URLs (like GitHub releases or direct hosting), use HEAD request
      const response = await fetch(url, { method: 'HEAD' });
      const size = response.headers.get('content-length');
      if (size) {
        const sizeInKB = Math.round(size / 1024);
        const sizeInMB = (size / (1024 * 1024)).toFixed(1);
        
        if (parseFloat(sizeInMB) > 0.9) {
          return `${sizeInMB} MB`;
        } else {
          return `${sizeInKB} KB`;
        }
      }
      return 'Unknown';
    } catch (error) {
      console.log(`Could not fetch file size for ${platform}:`, error);
      
      // Use fallbacks from our dynamic file size system
      return FILE_SIZES.platformFallbacks[platform] || 'Unknown';
    }
  };

  // Fetch file sizes on mount - now dynamic with intelligent fallbacks
  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL || ''}/release-manifest.json`)
      .then((response) => response.json())
      .then((data) => setManifest(data))
      .catch(() => setManifest(null));

    const updateFileSizes = async () => {
      // Define download URLs for size checking
      const downloadUrls = {
        windows: manifest?.downloads?.windows?.url || 'https://drive.google.com/uc?export=download&id=1bma-pqUCJjrwVBWBx5IvBglFors7USvs',
        android: manifest?.downloads?.android?.url || 'https://drive.google.com/uc?export=download&id=1C-F3DTwVoebNM8fuBqcGJyWdDmZH-1eP',
        macos: manifest?.downloads?.macos?.url || '/downloads/bervin-macos.dmg'
      };

      const sizes = {
        windows: await fetchFileSize(downloadUrls.windows, 'windows'),
        android: await fetchFileSize(downloadUrls.android, 'android'),
        macos: await fetchFileSize(downloadUrls.macos, 'macos')
      };
      
      setFileSizes(sizes);
    };

    updateFileSizes();
  }, [manifest]);

  // Download configurations
  const downloads = {
    windows: {
      name: manifest?.downloads?.windows?.label || 'Windows App',
      icon: SiWindows,
      fileSize: fileSizes.windows,
      version: manifest?.downloads?.windows?.version || '2.1.0',
      requirements: manifest?.downloads?.windows?.requirements || 'Windows 10 or later',
      downloadUrl: manifest?.downloads?.windows?.url || 'https://drive.google.com/uc?export=download&id=1bma-pqUCJjrwVBWBx5IvBglFors7USvs',
      color: 'from-blue-500 to-blue-600'
    },
    android: {
      name: manifest?.downloads?.android?.label || 'Android App',
      icon: SiAndroid,
      fileSize: fileSizes.android,
      version: manifest?.downloads?.android?.version || '2.1.0',
      requirements: manifest?.downloads?.android?.requirements || 'Android 6.0 or later',
      downloadUrl: manifest?.downloads?.android?.url || 'https://drive.google.com/uc?export=download&id=1C-F3DTwVoebNM8fuBqcGJyWdDmZH-1eP',
      color: 'from-green-500 to-green-600'
    },
    macos: {
      name: manifest?.downloads?.macos?.label || 'macOS App',
      icon: SiApple,
      fileSize: fileSizes.macos,
      version: manifest?.downloads?.macos?.version || '2.1.0',
      requirements: manifest?.downloads?.macos?.requirements || 'macOS 11.0 or later',
      downloadUrl: manifest?.downloads?.macos?.url || '/downloads/bervin-macos.dmg',
      color: 'from-gray-500 to-gray-600'
    }
  };

  const handleDownload = (platform, downloadUrl) => {
    setDownloadStarted(true);
    
    // Create download link with proper handling
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    
    // For development, force download
    if (window.location.hostname === 'localhost') {
      link.download = '';
    }
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    console.log(`Download started for ${platform} from ${downloadUrl}`);

    setTimeout(() => {
      setDownloadStarted(false);
    }, 3000);
  };

  const getSuggestedDownload = () => {
    if (detectedPlatform === 'android') return downloads.android;
    if (detectedPlatform === 'windows') return downloads.windows;
    if (detectedPlatform === 'macos') return downloads.macos;
    return downloads.windows;
  };

  const suggestedDownload = getSuggestedDownload();

  return (
    <section id="download" className="py-24 bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 relative overflow-hidden min-h-screen">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/5 w-96 h-96 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/5 w-80 h-80 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30 rounded-full mb-6">
            <FiDownload className="w-4 h-4 text-purple-400 mr-2" />
            <span className="text-purple-300 text-sm font-medium">Download Bervin</span>
          </div>
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight">
            Get Bervin for
            <span className="block bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
              Every Device
            </span>
          </h2>
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-12">
            Experience the full power of Bervin with our native applications, designed for optimal performance across all platforms.
          </p>
        </motion.div>

        {/* Suggested Download Card */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-2xl mx-auto mb-20"
        >
          <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-12 shadow-2xl">
            {/* Recommended Badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-full text-sm font-semibold shadow-lg flex items-center">
                <FiStar className="w-4 h-4 mr-2" />
                Recommended for your device
              </div>
            </div>

            <div className="text-center pt-6">
              {/* Platform Icon */}
              <div className={`inline-flex w-20 h-20 bg-gradient-to-r ${suggestedDownload.color} rounded-2xl items-center justify-center mb-6`}>
                {React.createElement(suggestedDownload.icon, { className: "w-10 h-10 text-white" })}
              </div>

              {/* Platform Name */}
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {suggestedDownload.name}
              </h3>
              <p className="text-lg text-slate-300 mb-8">
                Perfect for your {suggestedDownload.name.split(' ')[0].toLowerCase()} device
              </p>

              {/* App Details */}
              <div className="grid grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="text-sm text-slate-400 mb-1">Version</div>
                  <div className="text-xl font-bold text-white">{suggestedDownload.version}</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-slate-400 mb-1">Size</div>
                  <div className="text-xl font-bold text-white">{suggestedDownload.fileSize}</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-slate-400 mb-1">Requirements</div>
                  <div className="text-xl font-bold text-white">{suggestedDownload.requirements}</div>
                </div>
              </div>

              {/* Download Button */}
              <motion.button
                onClick={() => handleDownload(detectedPlatform, suggestedDownload.downloadUrl)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                disabled={downloadStarted}
                className={`inline-flex items-center px-8 py-4 bg-gradient-to-r ${suggestedDownload.color} text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-lg`}
              >
                {downloadStarted ? (
                  <>
                    <FiCheck className="w-6 h-6 mr-3" />
                    Download Started
                  </>
                ) : (
                  <>
                    <FiDownload className="w-6 h-6 mr-3" />
                    Download for {suggestedDownload.name.split(' ')[0]}
                  </>
                )}
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* All Platforms */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-white text-center mb-12">All Platforms</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {Object.entries(downloads).map(([platform, config], index) => (
              <motion.div
                key={platform}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                {/* Current Platform Indicator */}
                {platform === detectedPlatform && (
                  <div className="absolute -top-2 -right-2 w-4 h-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full animate-pulse"></div>
                )}

                <div className="text-center">
                  {/* Platform Icon */}
                  <div className={`inline-flex w-16 h-16 bg-gradient-to-r ${config.color} rounded-xl items-center justify-center mb-4`}>
                    {React.createElement(config.icon, { className: "w-8 h-8 text-white" })}
                  </div>

                  {/* Platform Name */}
                  <h4 className="text-xl font-bold text-white mb-2">{config.name}</h4>
                  
                  {/* Details */}
                  <div className="space-y-2 mb-6 text-sm text-slate-300">
                    <div className="flex justify-between">
                      <span>Version:</span>
                      <span className="font-semibold text-white">{config.version}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Size:</span>
                      <span className="font-semibold text-white">{config.fileSize}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Requires:</span>
                      <span className="font-semibold text-white">{config.requirements}</span>
                    </div>
                  </div>

                  {/* Download Button */}
                  <motion.button
                    onClick={() => handleDownload(platform, config.downloadUrl)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`w-full py-3 px-4 bg-gradient-to-r ${config.color} text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center`}
                  >
                    <FiDownload className="w-4 h-4 mr-2" />
                    Download
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Why Download Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center"
        >
          <h3 className="text-3xl font-bold text-white mb-12">Why Download the Native App?</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                icon: FiMonitor,
                title: 'Better Performance',
                description: 'Native apps run faster and smoother than web versions'
              },
              {
                icon: FiSmartphone,
                title: 'Easy Access',
                description: 'Quick and seamless access to all your conversations anytime'
              },
              {
                icon: FiCheck,
                title: 'Desktop Integration',
                description: 'Quick access from taskbar and system notifications'
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6"
              >
                <div className="inline-flex w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-xl font-bold text-white mb-3">{feature.title}</h4>
                <p className="text-slate-300 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto mt-16 pt-12 border-t border-white/10">
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">50K+</div>
              <div className="text-slate-400">Downloads</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">4.8/5</div>
              <div className="text-slate-400">Rating</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">✓</div>
              <div className="text-slate-400">Virus-free & Secure</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Download;
